<script setup lang="ts">
import type { DocumentFields, Field, Form, FormSchema } from './ElectricityBill.schema'
import type { AccessOpinionMode } from './electricityBill.utils'
import type { RegistrationDocument } from '~/services-v2/financial-bff/financial-bff.types'
import { cpf, formatCpfMasked, normalizeCpfDigits } from '@solfacil/frontend-utils'
import { SolInputText, SolLink, SolSelect } from '@solfacil/girassol'
import isEqual from 'lodash/isEqual'
import IconPlus from '~icons/material-symbols/add'
import FileStorageInput from '~/components/input/FileStorageInput.vue'
import { useFormalizationReadOnly } from '~/composables/financing/useFormalizationReadOnly'
import localstorage from '~/foundation/storage/localstorage'
import { RegistrationDocumentTypes } from '~/services-v2/financial-bff/__generated__/zeus'
import useEnergyConcessionaires from '~/store-v2/registration/useEnergyConcessionaires'
import useRegistration from '~/store-v2/registration/useRegistration'
import { isDocFieldDisabled } from '../../Formalization.utils'
import WhatIsBondModal from '../WhatIsBondModal.vue'
import AccessOpinionField from './AccessOpinionField.vue'
import AccessOpinionFieldSkeleton from './AccessOpinionFieldSkeleton.vue'
import { extractErrorsByField } from './ElectricityBill.schema'

interface ElectricityBillFields {
  is_under_customer_name?: boolean | null
  holder_cpf?: string | null
  distributor_id?: number | null
}

// Props
const props = defineProps<{
  form: Form
  disabled?: boolean
  accessOpinionMode?: AccessOpinionMode | null
  isAccessOpinionLoading?: boolean
}>()

const GRID_ACCESS_APPROVAL_DOCUMENT_TYPE = 'grid_access_approval'

// initial base hooks
const { t } = useI18n()
const route = useRoute<any>()

const projectId = computed(() => route.params.id as string)

const {
  data: registration,
} = useRegistration(projectId)

// fields
const [isUnderCustomerNameModel, isUnderCustomerNameProps] = props.form.defineField('is_under_customer_name')
const [holderCpfModel, holderCpfProps] = props.form.defineField('holder_cpf')
const [electricityBillModel, electricityBillProps] = props.form.defineField('electricity_bill_document')
const [holderDocumentModel, holderDocumentProps] = props.form.defineField('employment_proof_document')
const [additionalDocumentModel, additionalDocumentProps] = props.form.defineField('additional_document')
const [commentModel, commentProps] = props.form.defineField('comment')
const [accessOpinionDocModel, accessOpinionDocProps] = props.form.defineField('grid_access_approval_document')

const [, distributorIdProps] = props.form.defineField('distributor_id', state => ({
  props: {
    error: state.touched ? state.errors[0] : undefined,
  },
}))
const distributorError = computed(() => distributorIdProps.value?.error)

const { data: concessionaires } = useEnergyConcessionaires(projectId)

const concessionaireOptions = computed(() =>
  (concessionaires.value ?? []).map(item => ({ name: item.name, value: item.id })),
)

const selectedConcessionaire = computed(() => {
  const distributorId = props.form.values.distributor_id
  if (distributorId == null)
    return undefined

  return concessionaireOptions.value.find(option => Number(option.value) === Number(distributorId))
})

// initial variables and computed
const modalWhatIsBondOpen = ref(false)
const showAdditionalSection = ref(false)
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf']

const currentDocuments = reactive<Record<DocumentFields, RegistrationDocument | undefined>>({
  electricity_bill_document: undefined,
  employment_proof_document: undefined,
  additional_document: undefined,
  grid_access_approval_document: undefined,
})

const isStatusToReviewOrInProgress = computed(() => ['to_review', 'in_progress'].includes(registration.value?.status ?? ''))
const errors = computed(() => extractErrorsByField(registration.value?.errors))
const isSectionDisabled = computed(() => props.disabled || false)
// Terminal read-only mode (provided by the formalization step); false by default.
const isReadOnly = useFormalizationReadOnly()
const firstError = computed(() => Object.values(errors.value)[0])

function handleSelectConcessionaire(option?: { name: string, value: number }) {
  props.form.setFieldTouched('distributor_id', true)
  props.form.setFieldValue('distributor_id', option?.value)
}

// An error of the backend on the access opinion keeps the field visible and required (P-6)
const hasAccessOpinionError = computed(() =>
  !!errors.value.grid_access_approval_document || !!props.form.errors.value.grid_access_approval_document,
)

const showAccessOpinion = computed(() =>
  props.accessOpinionMode === 'SHOW'
  || props.accessOpinionMode === 'SHOW_REQUIRED'
  || hasAccessOpinionError.value,
)

const accessOpinionFieldMode = computed(() =>
  props.accessOpinionMode === 'SHOW_REQUIRED' || hasAccessOpinionError.value ? 'SHOW_REQUIRED' : 'SHOW',
)

const isAccessOpinionRequired = computed(() =>
  showAccessOpinion.value && accessOpinionFieldMode.value === 'SHOW_REQUIRED',
)

watch(isAccessOpinionRequired, (required) => {
  props.form.setFieldValue('grid_access_approval_required', required, false)
}, { immediate: true })

// A hidden field takes its document out of the registration, the new upload and the saved one (DC-4, DC-17).
// Watching the value too covers a hydration that arrives after the requirement result.
watch(
  [() => props.accessOpinionMode, () => props.form.values.grid_access_approval_document],
  ([mode, document]) => {
    if (mode !== 'HIDDEN' || hasAccessOpinionError.value || isSectionDisabled.value || isReadOnly.value)
      return

    if (document)
      props.form.setFieldValue('grid_access_approval_document', '', false)

    currentDocuments.grid_access_approval_document = undefined
  },
)

const radiosIsUnderCustomerName = [
  {
    name: 'electricityBillIsUnderCustomerName',
    value: true,
    label: `${t('formAddress.yesCountryside')}`,
  },
  {
    name: 'electricityBillIsUnderCustomerName',
    value: false,
    label: `${t('formAddress.noCountryside')}`,
  },
]

function handleOpenModalWhatIsBond() {
  modalWhatIsBondOpen.value = true
}

function handleShowAdditionalSection() {
  // Read-only mode: do not open the section when it has no saved content (hiding stays allowed).
  // Reopening is allowed when a document or comment exists, so it can still be viewed/downloaded.
  const hasAdditionalContent = !!currentDocuments.additional_document || !!props.form.values.comment
  if (isReadOnly.value && !showAdditionalSection.value && !hasAdditionalContent) {
    return
  }
  showAdditionalSection.value = !showAdditionalSection.value
}

watch(registration, (newValue, oldValue) => {
  if (isEqual(newValue, oldValue)) {
    return
  }

  if (newValue) {
    const data = newValue.project
    const electricityBill = data?.electricity_bill as ElectricityBillFields | undefined

    const electricityBillDoc = data?.documents?.find(doc => doc.type === RegistrationDocumentTypes.electricity_bill)
    const employmentProofDocument = data?.documents?.find(doc => doc.type === RegistrationDocumentTypes.employment_proof)
    const additionalDocument = data?.documents?.find(doc => doc.type === RegistrationDocumentTypes.additional)
    const accessOpinionDocument = data?.documents?.find(doc => (doc.type as string) === GRID_ACCESS_APPROVAL_DOCUMENT_TYPE)

    if (electricityBillDoc) {
      currentDocuments.electricity_bill_document = electricityBillDoc
      props.form.setFieldValue('electricity_bill_document', electricityBillDoc.docstorage_id)
    }

    if (employmentProofDocument) {
      currentDocuments.employment_proof_document = employmentProofDocument
      props.form.setFieldValue('employment_proof_document', employmentProofDocument.docstorage_id)
    }

    if (additionalDocument) {
      currentDocuments.additional_document = additionalDocument
      props.form.setFieldValue('additional_document', additionalDocument.docstorage_id)
    }

    if (accessOpinionDocument) {
      currentDocuments.grid_access_approval_document = accessOpinionDocument
      props.form.setFieldValue('grid_access_approval_document', accessOpinionDocument.docstorage_id)
    }

    props.form.setValues({
      is_under_customer_name: electricityBill?.is_under_customer_name ?? true,
      holder_cpf: formatCpfMasked(normalizeCpfDigits(electricityBill?.holder_cpf ?? '')),
      comment: data?.comment ?? undefined,
    } as FormSchema, false)

    if (electricityBill?.distributor_id != null) {
      props.form.setFieldValue('distributor_id', Number(electricityBill.distributor_id))
    }

    if (data?.comment || additionalDocument) {
      showAdditionalSection.value = true
    }

    const errs = extractErrorsByField(newValue.errors)

    setTimeout(() => { // to avoid concorrency with the form values
      Object.entries(errs).forEach(([key, value]) => {
        props.form.setFieldError(key as Field, value.title)
      })
    }, 500)
  }
}, { immediate: true })

watch(() => props.form.values.is_under_customer_name, (newValue, oldValue) => {
  // The form starts as "true" and is hydrated from the saved registration: when the saved answer is already "false",
  // the true -> false flip comes from the hydration, not from the user, so the modal must not open by itself
  const savedAnswer = (registration.value?.project?.electricity_bill as ElectricityBillFields | undefined)?.is_under_customer_name
  if (oldValue === true && newValue === false && savedAnswer !== false) {
    const storageKey = `electricity_bill_modal_shown_${projectId.value}`
    const hasShownModal = localstorage.get<boolean>(storageKey)

    if (!hasShownModal) {
      handleOpenModalWhatIsBond()
      localstorage.set(storageKey, true)
    }
  }
})
</script>

<template>
  <div v-if="registration">
    <!-- alert -->
    <SolAlert
      v-if="firstError && isStatusToReviewOrInProgress"
      id="feedback"
      class="mb-4"
      :feedback="(firstError as any)?.type ?? 'warning'"
      :title="firstError.title"
    >
      {{ firstError.description }}
    </SolAlert>
    <!--  -->

    <div class="flex flex-col items-start gap-6">
      <div class="flex flex-col items-start gap-4 w-full">
        <div class="flex flex-col items-start gap-2 w-full">
          <div class="flex flex-col items-start gap-4">
            <span class="font-highlight text-neutral-low-dark text-[16px] md:system:text-[20px]">
              {{ t('formalization.electricity_bill.title') }}
            </span>

            <div class="flex flex-col item-start gap-1">
              <SolRadioGroup
                id="is_under_customer_name"
                v-model="isUnderCustomerNameModel"
                :title="t('formalization.electricity_bill.form.electricity_bill_customer_name')"
                name="is_under_customer_name"
                direction="row"
                v-bind="isUnderCustomerNameProps"
                :disabled="isSectionDisabled"
                :radios="radiosIsUnderCustomerName"
                :error="props.form.errors.value.is_under_customer_name"
              />
              <div class="arrow-text">
                <span class="fonts-body-small-regular text-brand-primary-light">
                  {{ t('formalization.electricity_bill.info_bill') }}
                </span>
              </div>
              <div class="arrow-text">
                <span class="fonts-body-small-regular text-brand-primary-light">
                  {{ t('formalization.electricity_bill.info_files') }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <SolSelect
          id="distributor_id"
          :key="`distributor-${props.form.values.distributor_id ?? 'empty'}-${concessionaireOptions.length}`"
          name="distributor_id"
          class="w-full"
          :label="`${t('formalization.electricity_bill.energy_concessionaire')}*`"
          :searchable="true"
          :options="concessionaireOptions"
          :selected="selectedConcessionaire"
          :disabled="isSectionDisabled"
          :placeholder="t('formalization.electricity_bill.concessionaire_placeholder')"
          :error="distributorError"
          @update:selected="handleSelectConcessionaire($event as any)"
        />

        <div class="flex items-start gap-6 w-full">
          <FileStorageInput
            id="electricity_bill"
            v-model="electricityBillModel"
            :project-id="registration?.project_id"
            name="electricity_bill"
            :accept="ACCEPTED_FILE_TYPES.join(',')"
            :disabled="isSectionDisabled || isDocFieldDisabled(currentDocuments, 'electricity_bill_document')"
            :uploaded-file="currentDocuments.electricity_bill_document"
            v-bind="electricityBillProps"
            :placeholder="t('formDocument.placeholder')"
            :label="`${t('formalization.electricity_bill.title')}*`"
            :error="props.form.errors.value.electricity_bill_document"
            :enable-delete="isStatusToReviewOrInProgress && !isDocFieldDisabled(currentDocuments, 'electricity_bill_document')"
          />
        </div>

        <AccessOpinionFieldSkeleton v-if="isAccessOpinionLoading" />
        <AccessOpinionField
          v-else-if="showAccessOpinion"
          v-model="accessOpinionDocModel"
          v-bind="accessOpinionDocProps"
          :mode="accessOpinionFieldMode"
          :project-id="registration?.project_id"
          :concessionaire-name="selectedConcessionaire?.name ?? ''"
          :disabled="isSectionDisabled || isDocFieldDisabled(currentDocuments, 'grid_access_approval_document')"
          :error="props.form.errors.value.grid_access_approval_document"
          :uploaded-file="currentDocuments.grid_access_approval_document"
          :enable-delete="isStatusToReviewOrInProgress && !isDocFieldDisabled(currentDocuments, 'grid_access_approval_document')"
        />

        <div v-if="!props.form.values.is_under_customer_name" class="flex flex-col md:system:flex-row items-start gap-6 w-full">
          <div class="w-full">
            <FileStorageInput
              id="holder_document"
              v-model="holderDocumentModel"
              name="holder_document"
              :project-id="registration?.project_id"
              :label="`${t('formalization.electricity_bill.form.proof_link_document')}*`"
              v-bind="holderDocumentProps"
              :disabled="isSectionDisabled || isDocFieldDisabled(currentDocuments, 'employment_proof_document')"
              :placeholder="t('formDocument.placeholder')"
              :accept="ACCEPTED_FILE_TYPES.join(',')"
              :error="props.form.errors.value.employment_proof_document"
              :uploaded-file="currentDocuments.employment_proof_document"
              :enable-delete="isStatusToReviewOrInProgress && !isDocFieldDisabled(currentDocuments, 'employment_proof_document')"
            />

            <SolLink
              id="what-is-bond-link"
              size="medium"
              @click="handleOpenModalWhatIsBond"
            >
              {{ t('formalization.electricity_bill.what_is_proof_of_link') }}
            </SolLink>
          </div>

          <SolInputText
            id="holder_cpf"
            v-model="holderCpfModel"
            name="holder_cpf"
            class="w-full md:system:w-1/2"
            :label="`${t('formalization.electricity_bill.form.document_account_holder.label')}*`"
            v-bind="holderCpfProps"
            :mask="cpf.mask"
            :disabled="isSectionDisabled"
            :placeholder="t('formalization.electricity_bill.form.document_account_holder.placeholder')"
            :error="props.form.errors.value.holder_cpf"
          />
        </div>
      </div>
      <SolLink
        id="add-document-link"
        size="medium"
        @click="handleShowAdditionalSection"
      >
        <template v-if="!showAdditionalSection" #icon>
          <IconPlus />
        </template>
        <template v-else #icon>
          <IconPlus class="transform rotate-45" />
        </template>
        {{ showAdditionalSection ? 'Esconder documento e comentário adicional' : 'Adicionar documento e comentário adicional' }}
      </SolLink>
      <div v-if="showAdditionalSection" class="flex flex-col items-start gap-6 w-full">
        <FileStorageInput
          id="additional_document"
          v-model="additionalDocumentModel"
          name="additional_document"
          :project-id="registration.project_id"
          label="Documento adicional"
          v-bind="additionalDocumentProps"
          :disabled="isSectionDisabled || isDocFieldDisabled(currentDocuments, 'additional_document')"
          :placeholder="t('formDocument.placeholder')"
          :accept="ACCEPTED_FILE_TYPES.join(',')"
          :error="props.form.errors.value.additional_document"
          :uploaded-file="currentDocuments.additional_document || undefined"
          :enable-delete="isStatusToReviewOrInProgress && !isDocFieldDisabled(currentDocuments, 'additional_document')"
        />
        <SolInputTextarea
          id="comment"
          v-model="commentModel"
          name="comment"
          v-bind="commentProps"
          :disabled="isSectionDisabled"
          class="w-full"
          label="Deseja adicionar algum comentário?"
          :error="props.form.errors.value.comment"
        />
      </div>
    </div>

    <!--  -->
    <WhatIsBondModal
      :show="modalWhatIsBondOpen"
      @close="modalWhatIsBondOpen = false"
    />
    <!--  -->
  </div>
</template>

<style lang="scss" scoped>
.arrow-text::before {
  @apply text-brand-primary-light;
  content: '➔';
}

.error-document {
  :deep(.sol-download) {
    @apply border-feedback-negative-pure;
  }
}
</style>
